import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Alert, Badge, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AppLayout } from "@/components/AppLayout";
import { fetchNetsuiteMap } from "@/api/netsuiteMap";
import type { TargetType } from "@/api/netsuiteMap";
import { MapLegend, SystemMap } from "./netsuiteMap/SystemMap";
import { RoleMark, WhoCreatesTable } from "./netsuiteMap/WhoCreatesTable";
import { ChainLegend, DocumentChains } from "./netsuiteMap/DocumentChains";
import { Timeline } from "./netsuiteMap/Timeline";
import { Inventory } from "./netsuiteMap/Inventory";
import { OpenItems } from "./netsuiteMap/OpenItems";
import { CommentsDrawer, MAP_QUERY_KEY } from "./netsuiteMap/CommentsDrawer";
import { isNew, targetId, writes } from "./netsuiteMap/shared";
import type { Target } from "./netsuiteMap/shared";

// NetSuite Transaction Map: every NetSuite integration and how a transaction
// moves through our systems. Content comes from config/netsuite_map.yml; staff
// leave comments and suggestions on any integration, record row or open item.

function Section({ title, description, aside, children }: { title: string; description: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <Box component="section" sx={{ pt: 4, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} spacing={1}>
        <Box sx={{ maxWidth: "75ch" }}>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        {aside}
      </Stack>
      {children}
    </Box>
  );
}

export function NetsuiteMapPage() {
  const page = useQuery({ queryKey: MAP_QUERY_KEY, queryFn: fetchNetsuiteMap, retry: false });
  const [sysFilter, setSysFilter] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [target, setTarget] = useState<Target | null>(null);

  const data = page.data;
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    data?.comments.forEach((m) => {
      const k = targetId(m.target_type, m.target_key);
      c[k] = (c[k] ?? 0) + 1;
    });
    return c;
  }, [data]);

  const openThread = (type: TargetType) => (key: string) => {
    setTarget({ type, key });
    setDrawerOpen(true);
  };

  if (!data) {
    return (
      <AppLayout maxWidth="xl">
        {page.isError
          ? <Alert severity="error">Couldn&apos;t load the NetSuite map. Refresh to try again.</Alert>
          : <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>}
      </AppLayout>
    );
  }

  const { map } = data;
  const all = map.integrations;
  const inHouse = new Set(all.map((i) => i.owner).filter((o) => map.systems[o]?.kind === "in_house")).size;
  const openFeedback = data.comments.filter((c) => !c.resolved).length;
  const stats: [number, string, boolean?][] = [
    [all.length, "NetSuite integration points"],
    [all.filter(writes).length, "create or change NetSuite records"],
    [all.filter(isNew).length, "shipped since May 2026", true],
    [inHouse, "in-house apps plus ~40 scripts inside NetSuite"],
  ];

  return (
    <AppLayout maxWidth="xl">
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-start" }} spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ maxWidth: "80ch" }}>
          <Typography variant="overline" color="text.secondary">Finance systems · As of {dayjs(map.as_of).format("MMM D, YYYY")}</Typography>
          <Typography variant="h5">NetSuite Transaction Map</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{map.summary}</Typography>
        </Box>
        <Badge badgeContent={openFeedback} color="secondary" max={99}>
          <Button variant="outlined" startIcon={<ForumOutlinedIcon />} onClick={() => { setTarget(null); setDrawerOpen(true); }} sx={{ whiteSpace: "nowrap" }}>
            Comments and ideas
          </Button>
        </Badge>
      </Stack>

      <Paper variant="outlined" sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" }, overflow: "hidden" }}>
        {stats.map(([n, label, hot]) => (
          <Box key={label} sx={{ px: 2, py: 1.75, borderLeft: 1, borderTop: 1, borderColor: "divider", ml: "-1px", mt: "-1px" }}>
            <Typography sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1, fontVariantNumeric: "tabular-nums", color: hot ? "secondary.main" : "text.primary" }}>{n}</Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          </Box>
        ))}
      </Paper>

      <Section
        title="The system map"
        description="Each box is a system connected to NetSuite, and its orange line says what it writes there. Inside NetSuite, each record family lists the systems that create it: click a family to trace it, or click a box to filter the inventory below. The number on each box is how many integration points it owns."
        aside={<MapLegend />}
      >
        <SystemMap map={map} sysFilter={sysFilter} onSelectSystem={setSysFilter} />
      </Section>

      <Section
        title="Who creates each NetSuite record"
        description="Each row lists every system that touches that record. Creates means the system makes new records. Updates means it fills in fields on records made elsewhere. By hand means a person still keys it in NetSuite."
        aside={<Stack direction="row" spacing={1}><RoleMark role="creates" /><RoleMark role="updates" /><RoleMark role="by_hand" /></Stack>}
      >
        <WhoCreatesTable map={map} counts={counts} onComment={openThread("record")} />
      </Section>

      <Section
        title="Document chains, step by step"
        description="Each cycle is shown as the chain of NetSuite records it produces, with who or what creates each one. The labels show how much a person still has to do."
        aside={<ChainLegend />}
      >
        <DocumentChains chains={map.chains} />
      </Section>

      <Section
        title="What changed, and when"
        description="Dates are when the work merged to production code. The bar shows how many of the integration points in this map came out of each period."
      >
        <Timeline map={map} />
      </Section>

      <Section title="Integration inventory" description="Every NetSuite touchpoint found in code, filterable by cycle and direction.">
        <Inventory map={map} sysFilter={sysFilter} onClearSystem={() => setSysFilter(null)} counts={counts} onComment={openThread("integration")} />
      </Section>

      <Section title="Open items to review together" description="Places where the flow is still manual, or where something in code needs confirming in production.">
        <OpenItems items={map.open_items} counts={counts} onComment={openThread("open_item")} />
      </Section>

      <Stack spacing={0.75} sx={{ mt: 5, pt: 2, borderTop: 1, borderColor: "divider", maxWidth: "90ch" }}>
        {map.about.map((p) => <Typography key={p} variant="body2" color="text.secondary">{p}</Typography>)}
      </Stack>

      <CommentsDrawer
        open={drawerOpen}
        target={target}
        map={map}
        comments={data.comments}
        canResolve={data.can_resolve}
        onClose={() => setDrawerOpen(false)}
        onOpenTarget={setTarget}
        onShowAll={() => setTarget(null)}
      />
    </AppLayout>
  );
}
